function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { arrow, autoUpdate, flip, FloatingArrow, Placement, safePolygon, shift, useFloating, useHover, useInteractions } from '@floating-ui/react';
import { DisplaySize } from 'common/components/tooltip/types';
import { TooltipReact } from 'common/components/tooltip';
import styles from './new-tooltip.module.styl';
type Props = {
  content: string | React.ReactElement;
  size: DisplaySize;
  placement: Placement;
  useArrow?: boolean;
  /** Whether to show the tooltip or not. */
  enabled?: boolean;
  /** The element to add a tooltip to. */
  children?: React.ReactElement;
  /** If you want to anchor to something other than the child. NOTE: Prefer to use child though. */
  anchorRef?: HTMLElement;
};

/**
 * New Tooltip component using floating-ui.
 *
 * @param props set useArrow to true to use floating-ui arrow instead of tooltip.
 * @returns
 */
export function NewTooltip(props: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  const childRef = useRef<HTMLElement | null>(null);
  const arrowRef = useRef(null);
  const element = childRef.current || props.anchorRef;
  const enabled = (props.enabled !== undefined ? props.enabled : true) && props.content;
  const root = document.querySelector("#tooltips") || document.body;
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    placement: props.placement,
    // temporarily disabling flip and arrow middleware to handle compatibility with the existing Tooltip component
    // TODO: enable flip and arrow for all tooltip instances; test use case for boards
    middleware: [props.useArrow && flip({
      fallbackAxisSideDirection: "start",
      crossAxis: false
    }), shift(), props.useArrow && arrow({
      element: arrowRef
    })],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: element
    },
    open: showTooltip,
    onOpenChange: setShowTooltip
  });
  const hover = useHover(context, {
    handleClose: safePolygon()
  });
  const {
    getFloatingProps
  } = useInteractions([hover]);
  const clonedChild = props.children ? React.cloneElement(props.children, {
    ref: (el: any) => {
      var _props$children;
      childRef.current = el;

      // preserve any ref that might be passed in to the child
      const originalRef = (_props$children = props.children as any) === null || _props$children === void 0 ? void 0 : _props$children.ref;
      if (typeof originalRef === "function") {
        originalRef(el);
      } else if (originalRef) {
        originalRef.current = el;
      }
    }
  }) : null;
  return <>
      {clonedChild}
      {showTooltip && enabled && ReactDOM.createPortal(<div className={styles.tooltipContainer} ref={refs.setFloating} style={_objectSpread({}, floatingStyles)} {...getFloatingProps()}>
            {props.useArrow ? <div className={styles.tooltip}>
                <FloatingArrow ref={arrowRef} context={context} fill={"var(--inverted-component-background-color)"} />
                <div className={styles.tooltipContent}>{props.content}</div>
              </div> : <TooltipReact forceShow placement={props.placement as any} size={props.size}>
                {props.content}
              </TooltipReact>}
          </div>, root)}
    </>;
}